import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ProductPhoto } from '../ProductPhoto';
import { ProductShelfContent, ProductShelfContentOver, ShelfCarouselTitle, StyledPlusButton, styles } from './ProductShelfCarousel.styles';
import { ProductShelfCarouselProps, ProductShelfItemProps } from './types';
import { useProductShelfItem } from './ProductShelfCarousel.controller';
import { ReactComponent as Plus } from '@assets/icons/circle-plus.svg';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { InlineSpinner } from '@shared/components/TempSpinner';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { Carousel } from '../../../Carousel';
import { useTranslation } from '@shared/core';

export const ProductShelfCarousel = ({ productsList, title, isDialog, onClickCard, onClickAdd, sectionType }: ProductShelfCarouselProps) => {
  return (
    <Flex __css={styles.shelfCarousel}>
      <ShelfCarouselTitle
        __css={styles.shelfCarouselTitle}
        $isDialog={isDialog}
      >
        {title}
      </ShelfCarouselTitle>
      <Carousel
        totalNumberOfItems={productsList?.length ?? 0}
        enablePeek={false}
      >
        {productsList &&
          productsList.map(product => {
            return (
              <Carousel.Item key={product.id}>
                <ProductShelfItem
                  key={product.id}
                  productData={product}
                  onClickCard={onClickCard}
                  onClickAdd={onClickAdd}
                  sectionType={sectionType}
                />
              </Carousel.Item>
            );
          })}
      </Carousel>
    </Flex>
  );
};

ProductShelfCarousel.Skeleton = ({ isMobile }: { isMobile: boolean }) => {
  const elements = Array.from(Array(isMobile ? 2 : 5).keys());

  return (
    <Flex __css={styles.shelfCarousel}>
      <Box __css={styles.shelfCarouselTitle}>
        <SkeletonText
          rows={1}
          skeletonHeight={6}
          width={isMobile ? '100%' : 640}
          marginBottom={isMobile ? 2 : 6}
        />
        {isMobile && (
          <SkeletonText
            rows={1}
            skeletonHeight={6}
            width={'70%'}
            marginBottom={6}
          />
        )}
        <Flex>
          {elements.map(key => (
            <Box key={key}>
              <SkeletonThumbnail
                size={isMobile ? 220 : 336}
                marginRight={24}
                marginBottom={isMobile ? 4 : 2}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={5}
                marginBottom={2}
                width={100}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={5}
                marginBottom={2}
                width={isMobile ? 220 : 240}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={5}
                marginBottom={2}
                width={isMobile ? 140 : 200}
              />
              <SkeletonText
                rows={1}
                skeletonHeight={5}
                marginBottom={2}
                width={80}
              />
            </Box>
          ))}
        </Flex>
      </Box>
    </Flex>
  );
};

const ProductShelfItem = (props: ProductShelfItemProps) => {
  const { handleClickLink, handleClickAdd, isBeingAdded } = useProductShelfItem(props);
  const { formatCurrency } = useCurrencyFormatter();
  const { t: translations } = useTranslation('catalogRegistry');
  const { message } = translations('salesPrice');

  const { productData } = props;
  const { title, brand, photos, price, isFullPrice, fullPrice } = productData;
  const photoUrl = photos[0]?.url;
  const formattedSalePrice = formatCurrency({
    priceFloatingPointDecimalString: price?.floatingPointDecimalString || '',
    priceCurrencyCode: price?.currency?.code,
    formatForm: 'short',
  });
  const formattedFullPrice = formatCurrency({
    priceFloatingPointDecimalString: fullPrice?.floatingPointDecimalString || '',
    priceCurrencyCode: fullPrice?.currency?.code,
    formatForm: 'short',
  });

  return (
    <Box __css={styles.productShelfContainer}>
      <ProductShelfContent __css={styles.productShelfContent}>
        <ProductShelfContentOver
          as="button"
          onClick={handleClickLink}
          __css={styles.productShelfContentOver}
        >
          <ProductPhoto
            photoUrl={photoUrl}
            isCoverImage={false}
          />
          <Box
            position={'relative'}
            paddingTop={5}
            textAlign={'left'}
          >
            <StyledPlusButton
              as="button"
              onClick={handleClickAdd}
              __css={styles.plusButton}
              disabled={isBeingAdded}
            >
              {isBeingAdded ? <InlineSpinner /> : <Plus />}
            </StyledPlusButton>
            {brand && (
              <Box
                color={'mono10'}
                width={280}
                fontWeight={400}
                fontSize={15}
                marginBottom={1}
              >
                {brand.name}
              </Box>
            )}
            <Box
              color={'mono14'}
              width={'100%'}
              fontWeight={600}
              fontSize={17}
              marginBottom={1}
              whiteSpace="break-spaces"
              paddingRight={6}
            >
              {title}
            </Box>
            {price && (
              <TextV2
                fontWeight={400}
                fontSize={17}
                component="p"
              >
                <Box
                  as="span"
                  color={'mono10'}
                  textDecoration={!isFullPrice ? 'line-through' : ''}
                  marginRight={3}
                >
                  {formattedFullPrice}
                </Box>
                {!isFullPrice && (
                  <Box
                    as="span"
                    color="#D80B0B"
                  >
                    {message()} {formattedSalePrice}
                  </Box>
                )}
              </TextV2>
            )}
          </Box>
        </ProductShelfContentOver>
      </ProductShelfContent>
    </Box>
  );
};
